import { ItemPredicate, ItemRenderer, Select } from '@blueprintjs/select';
import { Button, MenuItem } from '@blueprintjs/core';
import styles from './QuerySelect.module.css';

import { classNames } from '../../utils/classNames';

export type IData = { disabled?: boolean } | unknown;

export interface IQuerySelectProps<T extends IData> {
  data: T[];
  value: T | undefined;
  stringify: (value: T) => string;
  onSelect: (value: T) => void;
  className?: string | undefined;
  disabled?: boolean | undefined;
  fill?: boolean | undefined;
  filterable?: boolean | undefined;
}

export function QuerySelect<T extends IData>({
  data,
  value,
  stringify,
  onSelect,
  disabled,
  className,
  fill,
  filterable,
}: IQuerySelectProps<T>) {
  const renderer: ItemRenderer<T> = (
    value,
    { handleClick, handleFocus, modifiers }
  ) => (
    <MenuItem
      text={stringify(value)}
      roleStructure="listoption"
      active={modifiers.active}
      key={stringify(value)}
      onClick={handleClick}
      onFocus={handleFocus}
      disabled={
        value !== null && typeof value === 'object' && 'disabled' in value
          ? value['disabled'] === true
          : undefined
      }
    />
  );

  const filterItem: ItemPredicate<T> = (query, item, _index, exactMatch) => {
    const normalizedTitle = stringify(item).toLowerCase();
    const normalizedQuery = query.toLowerCase();
    if (exactMatch) {
      return normalizedTitle === normalizedQuery;
    } else {
      return normalizedTitle.indexOf(normalizedQuery) >= 0;
    }
  };

  const textToDisplay = value ? stringify(value) : '';

  return (
    <Select
      filterable={Boolean(filterable)}
      items={data ?? []}
      itemRenderer={renderer}
      activeItem={value}
      onItemSelect={onSelect}
      disabled={disabled}
      fill={fill}
      itemPredicate={filterItem}
    >
      <Button
        small
        text={<span className={styles.text}>{textToDisplay}</span>}
        rightIcon="caret-down"
        title={textToDisplay}
        disabled={disabled}
        className={classNames(className ?? '', styles.button)}
        fill={fill}
      />
    </Select>
  );
}
